/**
 * Poseidon dashboard — dashboard-wide data clock.
 *
 * Holds the newest `as_of` timestamp any successful fetch has reported, plus the
 * provenance of that data. This is what the persistent "last data timestamp"
 * indicator reads, so the staleness badge reflects the whole dashboard rather
 * than only whichever panel happens to poll fastest.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { DataSourceMode } from '../api/types';
import { newestTimestamp } from '../lib/staleness';

export interface DataClockValue {
  lastDataAt: string | null;
  /** Provenance of the payload that produced `lastDataAt`. */
  provenance: 'live' | 'replay' | 'synthetic' | null;
  /** Count of synthetic payloads seen, so the UI can prove mock data is in use. */
  syntheticReports: number;
  report: (asOf: string, provenance: 'live' | 'replay' | 'synthetic') => void;
  mode: DataSourceMode;
}

const DataClockContext = createContext<DataClockValue | null>(null);

export function DataClockProvider({ mode, children }: { mode: DataSourceMode; children: ReactNode }): JSX.Element {
  const [lastDataAt, setLastDataAt] = useState<string | null>(null);
  const [provenance, setProvenance] = useState<'live' | 'replay' | 'synthetic' | null>(null);
  const [syntheticReports, setSyntheticReports] = useState(0);
  const latestRef = useRef<string | null>(null);

  const report = useCallback((asOf: string, nextProvenance: 'live' | 'replay' | 'synthetic') => {
    const newest = newestTimestamp([latestRef.current, asOf]);
    if (newest !== latestRef.current) {
      latestRef.current = newest;
      setLastDataAt(newest);
    }
    // Provenance follows the newest payload; if any payload is synthetic the
    // banner stays up, because mixed data must never look fully live.
    setProvenance((prev) => {
      if (nextProvenance === 'synthetic') return 'synthetic';
      return prev === 'synthetic' ? 'synthetic' : nextProvenance;
    });
    if (nextProvenance === 'synthetic') setSyntheticReports((n) => n + 1);
  }, []);

  const value = useMemo<DataClockValue>(
    () => ({ lastDataAt, provenance, syntheticReports, report, mode }),
    [lastDataAt, provenance, syntheticReports, report, mode],
  );

  return <DataClockContext.Provider value={value}>{children}</DataClockContext.Provider>;
}

export function useDataClock(): DataClockValue {
  const value = useContext(DataClockContext);
  if (value === null) {
    throw new Error('useDataClock must be used inside a DataClockProvider');
  }
  return value;
}

/** Safe variant for components that may render outside the provider (e.g. tests). */
export function useOptionalDataClock(): DataClockValue | null {
  return useContext(DataClockContext);
}
